import { z } from "zod";
import { IntlShape } from "react-intl";

import {
    MAX_NAME_LENGTH,
    MAX_COMPANY_NAME_LENGTH,
    MAX_JOB_TITLE_LENGTH,
    PASSWORD_MIN_LENGTH
} from "../../../constants";
import validationMessages from "../../../validationMessages";
import { safeTextValidator } from "./safeTextValidator";

const PASSWORD_PATTERN = new RegExp(
    `^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[^a-zA-Z0-9]).{${PASSWORD_MIN_LENGTH},}$`
);

export function getCompanyNameZodField(intl: IntlShape) {
    return z.string()
        .min(1, intl.formatMessage(validationMessages.companyNameRequired))
        .max(MAX_COMPANY_NAME_LENGTH, intl.formatMessage(
            validationMessages.companyNameTooLong,
            { maxCompanyNameLength: MAX_COMPANY_NAME_LENGTH }
        ))
        .refine(
            safeTextValidator,
            intl.formatMessage(
                validationMessages.companyNameInvalidChars
            )
        );
}

export function getJobTitleZodField(intl: IntlShape) {
    return z.string()
        .trim()
        .min(1, intl.formatMessage(validationMessages.jobTitleRequired))
        .max(MAX_JOB_TITLE_LENGTH, intl.formatMessage(
            validationMessages.jobTitleTooLong,
            { maxJobTitleLength: MAX_JOB_TITLE_LENGTH }
        ))
        .refine(
            safeTextValidator,
            intl.formatMessage(validationMessages.jobTitleInvalidChars)
        );
}

export function getAcceptPrivacyPolicyZodField(intl: IntlShape) {
    return z.literal(true, {
        errorMap: () => ({
            message: intl.formatMessage(
                validationMessages.termsMustBeAccepted
            )
        })
    });
}

export function getRegistrationSchema(intl: IntlShape, hasDeveloperSandbox: boolean) {
    const baseSchema = z.object({
        accountName: z.string()
            .min(1, intl.formatMessage(validationMessages.nameRequired))
            .max(MAX_NAME_LENGTH, intl.formatMessage(
                validationMessages.nameTooLong, { maxNameLength: MAX_NAME_LENGTH }
            ))
            .refine(
                val => val.trim().includes(" "),
                intl.formatMessage(validationMessages.fullNameRequired)
            ),
        companyName: hasDeveloperSandbox
            ? getCompanyNameZodField(intl)
            : z.string(),
        jobTitle: hasDeveloperSandbox
            ? getJobTitleZodField(intl)
            : z.string(),
        email: z.string()
            .min(1, intl.formatMessage(validationMessages.emailRequired))
            .email(intl.formatMessage(validationMessages.emailWrong)),
        password: z.string()
            .min(1, intl.formatMessage(validationMessages.passwordRequired))
            .regex(PASSWORD_PATTERN, intl.formatMessage(
                validationMessages.passwordRules,
                { minLength: PASSWORD_MIN_LENGTH }
            )),
        confirmPassword: z.string()
            .min(1, intl.formatMessage(validationMessages.passwordRequired)),
        acceptPrivacyPolicy: getAcceptPrivacyPolicyZodField(intl)
    });

    return baseSchema.refine(
        data => data.password === data.confirmPassword,
        {
            message: intl.formatMessage(
                validationMessages.passwordDoNotMatch
            ),
            path: ["confirmPassword"]
        }
    );
}
